import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { openSupportChatAndGo } from '../utils/supportChat';

function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [processingId, setProcessingId] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('Todos');
  const [onlineIds, setOnlineIds] = useState(new Set());

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      // La presencia real está en driver_locations, no en drivers.status: la
      // app publica y borra ese documento al conectarse y desconectarse, y
      // manda un latido cada 3 minutos. Leer solo drivers.status mostraba
      // "Desconectado" a conductores que estaban trabajando.
      const [snapshot, locationsSnap] = await Promise.all([
        getDocs(query(collection(db, 'drivers'), where('approvalStatus', '==', activeTab))),
        getDocs(collection(db, 'driver_locations')),
      ]);

      const activos = new Set();
      const haceCincoMinutos = Date.now() - 5 * 60 * 1000;
      locationsSnap.forEach(doc => {
        const ts = doc.data().updatedAt;
        const millis = ts?.toMillis ? ts.toMillis() : ts;
        if (millis && millis > haceCincoMinutos) activos.add(doc.id);
      });
      setOnlineIds(activos);

      const driversData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDrivers(driversData);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [activeTab]);

  // Al cambiar de pestaña, el país elegido puede no existir entre los nuevos
  // conductores; sin esto el filtro los escondería todos.
  useEffect(() => {
    if (selectedCountry !== 'Todos' && !availableCountries.includes(selectedCountry)) {
      setSelectedCountry('Todos');
    }
  }, [drivers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApproval = async (driverId, action) => {
    setProcessingId(driverId);
    try {
      const functions = getFunctions();
      const fnName = action === 'approve' ? 'approveDriver' : 'rejectDriver';
      const manageDriver = httpsCallable(functions, fnName);
      
      await manageDriver({ driverId, reason: "Revisado por administrador" });
      setDrivers(drivers.filter(d => d.id !== driverId));
    } catch (error) {
      console.error(`Error ${action} driver:`, error);
      alert(`Error: ${error.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (targetUid) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar a este conductor? Esta acción es irreversible y borrará todo su rastro.")) return;

    setProcessingId(targetUid);
    try {
      const functions = getFunctions();
      const deleteUserCallable = httpsCallable(functions, 'deleteUser');
      await deleteUserCallable({ targetUid });
      setDrivers(drivers.filter(d => d.id !== targetUid));
      alert("Conductor eliminado exitosamente.");
    } catch (error) {
      console.error("Error deleting driver:", error);
      alert("Error al eliminar conductor: " + error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const tabClass = (tab) => {
    const isActive = activeTab === tab;
    if (!isActive) return 'tab-btn';
    return `tab-btn active-${tab}`;
  };

  // Debe coincidir con CountryParser de la app (utils/country_parser.dart),
  // que es quien rellena drivers.country. Con la lista corta anterior, elegir
  // cualquier país escondía a los conductores del resto de países, y los de
  // EE.UU. aparecían clasificados como República Dominicana.
  const getCountryFromPhone = (phone) => {
    if (!phone) return 'Desconocido';
    if (phone.startsWith('+58')) return 'Venezuela';
    if (phone.startsWith('+57')) return 'Colombia';
    if (phone.startsWith('+56')) return 'Chile';
    if (phone.startsWith('+54')) return 'Argentina';
    if (phone.startsWith('+51')) return 'Perú';
    if (phone.startsWith('+593')) return 'Ecuador';
    if (phone.startsWith('+55')) return 'Brasil';
    if (phone.startsWith('+52')) return 'México';
    if (phone.startsWith('+591')) return 'Bolivia';
    if (phone.startsWith('+595')) return 'Paraguay';
    if (phone.startsWith('+598')) return 'Uruguay';
    if (phone.startsWith('+507')) return 'Panamá';
    if (phone.startsWith('+506')) return 'Costa Rica';
    if (phone.startsWith('+1809') || phone.startsWith('+1829') || phone.startsWith('+1849')) {
      return 'República Dominicana';
    }
    if (phone.startsWith('+1')) return 'EE.UU./Canadá';
    if (phone.startsWith('+34')) return 'España';
    return 'Desconocido';
  };

  // El examen guarda el país como código ISO (certification_provider.dart),
  // mientras drivers.country guarda el nombre. Se traduce para no mostrar
  // "CERTIFICADO VE" en la ficha.
  const nombrePais = (codigo) => ({
    VE: 'Venezuela', CO: 'Colombia', BR: 'Brasil',
    PE: 'Perú', MX: 'México', DO: 'República Dominicana',
  }[codigo] || codigo);

  // Solo los países que de verdad tienen conductores, para que el filtro no
  // ofrezca opciones vacías ni esconda a nadie.
  const availableCountries = [
    'Todos',
    ...Array.from(
      new Set(drivers.map(d => d.country || getCountryFromPhone(d.phone)))
    ).sort((a, b) => a.localeCompare(b, 'es')),
  ];

  const filteredDrivers = drivers.filter(d => {
    if (selectedCountry === 'Todos') return true;
    const driverCountry = d.country || getCountryFromPhone(d.phone);
    return driverCountry === selectedCountry;
  });

  return (
    <div className="animate-fade-in">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 className="page-title">Gestión de Conductores</h2>
          <p className="page-subtitle">Revisa y aprueba solicitudes de nuevos conductores</p>
        </div>
        <div>
          <select 
            className="input-field" 
            style={{ minWidth: '150px' }}
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            {availableCountries.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="tabs">
        <button onClick={() => setActiveTab('pending')} className={tabClass('pending')}>
          Pendientes
        </button>
        <button onClick={() => setActiveTab('approved')} className={tabClass('approved')}>
          Aprobados
        </button>
        <button onClick={() => setActiveTab('rejected')} className={tabClass('rejected')}>
          Rechazados
        </button>
      </div>

      {loading ? (
        <div style={{display:'flex', gap:'1rem'}}>
          <div className="skeleton-card" style={{flex:1, height:'120px'}} />
        </div>
      ) : (
        <div style={{display:'flex', flexDirection:'column', gap:'1rem'}}>
          {filteredDrivers.length > 0 ? filteredDrivers.map(driver => (
            <div key={driver.id} className="driver-card">
              <div className="driver-photo">
                {driver.photoUrl ? (
                  <img src={driver.photoUrl} alt="Driver" />
                ) : '👤'}
              </div>
              
              <div className="driver-details">
                <div>
                  <p className="detail-label">Conductor</p>
                  <p className="detail-value">{driver.name || 'Sin Nombre'}</p>
                  <p className="text-sm text-secondary">{driver.email}</p>
                  <p className="text-sm text-secondary">
                    {driver.phone}
                    {driver.phoneVerified === true ? (
                      <span title="Teléfono verificado por OTP"
                            style={{color: '#10b981', marginLeft: '4px', fontWeight: 'bold'}}>✓</span>
                    ) : (
                      <span title="Teléfono sin verificar"
                            style={{color: '#f59e0b', marginLeft: '4px'}}>sin verificar</span>
                    )}
                  </p>
                  <div style={{display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'4px'}}>
                    <span className="badge badge-success">
                      {driver.country || getCountryFromPhone(driver.phone)}
                    </span>
                    {driver.certified === true && (
                      <span
                        title={`Examen de tránsito aprobado${driver.certCountry ? ` (${nombrePais(driver.certCountry)})` : ''}`}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          fontSize: '0.65rem',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontWeight: 'bold',
                          letterSpacing: '0.5px',
                          alignSelf: 'center',
                        }}>
                        CERTIFICADO
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="detail-label">Registro</p>
                  <p className="text-sm" style={{marginTop:'4px'}}>
                    {driver.createdAt?.toDate ? new Date(driver.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="detail-label">Vehículo</p>
                  <p className="detail-value" style={{color:'var(--accent-orange)'}}>
                    {driver.motorcycle?.plate || 'Sin placa'}
                  </p>
                  <p className="text-sm">{driver.motorcycle?.model || 'Modelo desconocido'}</p>
                </div>
              </div>

              {activeTab === 'pending' && (
                <div className="driver-actions">
                  <button 
                    disabled={processingId === driver.id}
                    onClick={() => handleApproval(driver.id, 'approve')}
                    className="btn btn-success"
                  >
                    {processingId === driver.id ? '...' : '✓ Aprobar'}
                  </button>
                  <button 
                    disabled={processingId === driver.id}
                    onClick={() => handleApproval(driver.id, 'reject')}
                    className="btn btn-danger"
                  >
                    ✗ Rechazar
                  </button>
                </div>
              )}
              
              {activeTab === 'approved' && (
                <div className="driver-stats">
                  <span className={`badge ${onlineIds.has(driver.id) ? 'badge-success' : 'badge-warning'}`}>
                    {!onlineIds.has(driver.id)
                      ? 'Desconectado'
                      : driver.status === 'busy' ? 'En Viaje' : 'En Línea'}
                  </span>
                  <p className="text-sm" style={{marginTop:'8px'}}>Viajes: <span className="font-bold">{driver.totalTrips || 0}</span></p>
                  <p className="text-sm" style={{color:'var(--accent-amber)'}}>★ {(driver.rating || 5).toFixed(1)}</p>
                </div>
              )}

              <div className="driver-stats" style={{marginLeft: 'auto', borderLeft: '1px solid var(--border-color)', paddingLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                <button
                  onClick={() => openSupportChatAndGo(navigate, driver.id, 'driver')}
                  style={{
                    background: 'rgba(6, 182, 212, 0.1)',
                    color: 'var(--accent-cyan)',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}>
                  Abrir chat
                </button>
                <button 
                  disabled={processingId === driver.id}
                  onClick={() => handleDelete(driver.id)}
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-md)',
                    cursor: processingId === driver.id ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}>
                  Eliminar
                </button>
              </div>
            </div>
          )) : (
            <div className="card" style={{padding:'3rem', textAlign:'center'}}>
              <div className="empty-icon">📋</div>
              <p className="text-secondary">No hay conductores en la categoría "{activeTab}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Drivers;
